import React, { useEffect, useState } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import { useSelector } from "react-redux";
import { ToastContainer, toast } from "react-toastify";
import { Box, CircularProgress, Typography } from "@material-ui/core";

import { PATHS } from "./constants/paths";
import { ALERT_TEXT } from "./constants/titles";
import {
  clearStoredToken,
  expireSession,
  getStoredToken,
  onAuthExpired,
  onAuthRecheck,
  readUsableToken,
  resolvePostLoginPath,
} from "./utils/authSession";
import { SESSION_INVALID, validateSession } from "./dataProvider/sessionCheck";

import TopBar from "./components/TopBar";
import Venues from "./pages/Venues";
import Teams from "./pages/Teams";
import Login from "./pages/Login";
import Ads from "./pages/Ads";
import GoverningBodies from "./pages/GoverningBodies";
import QualifyingScores from "./pages/QualifyingScores";
import MessageTemplates from "./pages/MessageTemplates";
import MessageTemplateEdit from "./pages/MessageTemplates/MessageTemplateEdit";
import VariableRequests from "./pages/MessageTemplates/VariableRequests";
import Tools from "./pages/Tools";
import RuleGroups from "./pages/Rules/RuleGroups";
import RuleSets from "./pages/Rules/RuleSets";
import RuleSetEditor from "./pages/Rules/RuleSetEditor";

const SESSION_EXPIRED_TOAST_ID = "session-expired";

/** Shown while the stored token is being confirmed with the server, instead of the admin UI. */
const CheckingSession = () => (
  <Box
    role="status"
    aria-live="polite"
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    minHeight="100vh"
  >
    <CircularProgress aria-hidden="true" />
    <Box mt={2}>
      <Typography variant="body1">Checking your session…</Typography>
    </Box>
  </Box>
);

/** Sends anonymous visitors to login, remembering where they were headed. */
const RequireAuth = ({ token }) => {
  const location = useLocation();
  if (!token) {
    return <Navigate to={PATHS.LOGIN} replace state={{ from: location }} />;
  }
  return <Outlet />;
};

const LoginRoute = ({ token }) => {
  const location = useLocation();
  if (token) {
    return <Navigate to={resolvePostLoginPath(location.state?.from)} replace />;
  }
  return <Login />;
};

function App() {
  const { user } = useSelector((state) => state.user);
  // An expired or undecodable token is discarded here, so it never counts as "logged in".
  const [initialToken] = useState(readUsableToken);
  const [token, setToken] = useState(initialToken);
  const [checking, setChecking] = useState(Boolean(initialToken));

  const logOut = () => {
    clearStoredToken();
    setToken(getStoredToken());
  };

  useEffect(() => {
    setToken(getStoredToken());
  }, [user]);

  // A token that looks fine can still be refused by the server (for example after a password
  // change). Confirm it once before rendering any admin screen.
  useEffect(() => {
    if (!initialToken) return undefined;
    let active = true;
    validateSession().then((result) => {
      if (!active) return;
      if (result === SESSION_INVALID) expireSession();
      setChecking(false);
    });
    return () => {
      active = false;
    };
  }, [initialToken]);

  useEffect(() => {
    const offExpired = onAuthExpired(() => {
      setToken(null);
      toast.info(ALERT_TEXT.SESSION_EXPIRED, {
        toastId: SESSION_EXPIRED_TOAST_ID,
      });
    });

    let rechecking = false;
    const offRecheck = onAuthRecheck(async () => {
      if (rechecking) return;
      rechecking = true;
      try {
        if ((await validateSession()) === SESSION_INVALID) expireSession();
      } finally {
        rechecking = false;
      }
    });

    return () => {
      offExpired();
      offRecheck();
    };
  }, []);

  if (checking) {
    return <CheckingSession />;
  }

  return (
    <BrowserRouter>
      {token && <TopBar logOut={logOut} />}
      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        closeOnClick
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
      <Routes>
        <Route element={<RequireAuth token={token} />}>
          <Route index element={<Navigate to={PATHS.TEAMS} replace />} />
          <Route path={PATHS.VENUES} element={<Venues />} />
          <Route path={PATHS.TEAMS} element={<Teams />} />
          <Route path={PATHS.ADS} element={<Ads />} />
          <Route path={PATHS.GOVERNING_BODIES} element={<GoverningBodies />} />
          <Route
            path={PATHS.QUALIFYING_SCORES}
            element={<QualifyingScores />}
          />
          <Route
            path={PATHS.MESSAGE_TEMPLATES}
            element={<MessageTemplates />}
          />
          <Route path={PATHS.TOOLS} element={<Tools />} />
          <Route path={PATHS.RULES} element={<RuleGroups />} />
          <Route
            path={`${PATHS.RULES}/groups/:groupId`}
            element={<RuleSets />}
          />
          <Route
            path={`${PATHS.RULES}/groups/:groupId/sets/new`}
            element={<RuleSetEditor />}
          />
          <Route
            path={`${PATHS.RULES}/sets/:setId`}
            element={<RuleSetEditor />}
          />
          <Route
            path={`${PATHS.MESSAGE_TEMPLATES}/variable-requests`}
            element={<VariableRequests />}
          />
          <Route
            path={`${PATHS.MESSAGE_TEMPLATES}/:id`}
            element={<MessageTemplateEdit />}
          />
          <Route
            path={`${PATHS.MESSAGE_TEMPLATES}/:id/:localeId`}
            element={<MessageTemplateEdit />}
          />
        </Route>
        <Route path={PATHS.LOGIN} element={<LoginRoute token={token} />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
